import AccountProfile from "@/components/account/AccountProfile";
import SecureShieldAccountPanel from "@/components/account/SecureShieldAccountPanel";
import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowUpRight, Download, ShieldCheck } from "lucide-react";
import SEO from "@/components/SEO";
import ProviderButton from "@/components/identity/ProviderButton";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import { useSecureMacProAccount } from "@/context/SecureMacProAccountContext";
import SecureMacProAccountPanel from "@/components/account/SecureMacProAccountPanel";
import SecureAIAccountPanel from "@/components/account/SecureAIAccountPanel";
import FortressAccountPanel from "@/components/account/FortressAccountPanel";
import { customerApi } from "@/lib/customerApi";
import { API, formatApiError } from "@/lib/api";

const accountPath = (pathname) => pathname.replace(/\/+$/, "");

function AuthForms() {
  const { login, register } = useCustomerAuth();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [productOffersConsent, setProductOffersConsent] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [providers, setProviders] = useState([]);
  const [registrationEnabled, setRegistrationEnabled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    let active = true;
    customerApi
      .get("/customers/auth/providers")
      .then(({ data }) => {
        if (!active) return;
        setProviders(data.providers || []);
        const canRegister = data.email_password?.registration_enabled === true;
        setRegistrationEnabled(canRegister);
        if (!canRegister) {
          setMode("login");
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  const providerHref = (path) => {
    const returnTo = ["/account/link-store", "/account/approve-device", "/account/profile"].includes(accountPath(location.pathname))
      ? `${accountPath(location.pathname)}${location.search}`
      : "/account";
    const target = new URL(path, API.startsWith("http") ? API : window.location.origin);
    target.searchParams.set("return_to", returnTo);
    return API.startsWith("http") ? target.toString() : `${target.pathname}${target.search}`;
  };

  const submit = useCallback(
    async (e) => {
      e.preventDefault();
      setError("");
      setNotice("");
      setBusy(true);
      try {
        if (mode === "login") {
          await login(email, password);
        } else if (mode === "forgot") {
          await customerApi.post("/customers/password/request-reset", {email});
          setNotice("If this is an eligible password account, we'll email a reset link. If you use Apple, Google or Microsoft, sign in with that provider.");
        } else {
          const result = await register(email, password, name, productOffersConsent);
          if (result.verification_required) {
            setMode("login");
            setNotice("Check your email for a one-time verification link before signing in.");
          }
        }
      } catch (err) {
        setError(formatApiError(err));
      } finally {
        setBusy(false);
      }
    },
    [mode, email, password, name, productOffersConsent, login, register]
  );

  return (
    <div className="mx-auto max-w-md border border-ink-100 bg-white p-7 md:p-9">
      <div className="lf-eyebrow">
        {mode === "forgot" ? "Password recovery" : mode === "login" ? "Sign in" : "Create your account"}
      </div>
      <h1 className="mt-3 font-display text-3xl font-black">
        {mode === "forgot" ? "Reset your password." : mode === "login" ? "Your LightningFlow account." : "One account, every product."}
      </h1>
      <p className="mt-3 text-sm leading-relaxed text-ink-600">
        {mode === "forgot" ? "Enter the email used for password sign-in. We'll send a one-time reset link if the account is eligible." : mode === "login"
          ? "One account for your LightningFlow products."
          : "One LightningFlow identity for every product, purchase, subscription, download and device."}
      </p>

      {providers.length ? (
        <div className="mt-7 space-y-3" aria-label="Sign in providers">
          {providers.map((provider) => (
            <ProviderButton
              key={provider.id}
              provider={provider.id}
              label={provider.label}
              href={providerHref(provider.start_path)}
            />
          ))}
          <div className="flex items-center gap-3 py-1 text-[10px] uppercase tracking-[.18em] text-ink-400">
            <span className="h-px flex-1 bg-ink-100" /> or use email <span className="h-px flex-1 bg-ink-100" />
          </div>
        </div>
      ) : null}

      <form className="mt-7 space-y-4" onSubmit={submit}>
        {mode === "register" && (
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[.18em] text-ink-500">Name</label>
            <input
              className="mt-2 w-full border border-ink-200 px-4 py-3 text-sm"
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="account-name-input"
            />
          </div>
        )}
        <div>
          <label className="font-mono text-[10px] uppercase tracking-[.18em] text-ink-500">Email</label>
          <input
            className="mt-2 w-full border border-ink-200 px-4 py-3 text-sm"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="account-email-input"
          />
        </div>
        {mode !== "forgot" ? <div>
          <label className="font-mono text-[10px] uppercase tracking-[.18em] text-ink-500">Password</label>
          <input
            className="mt-2 w-full border border-ink-200 px-4 py-3 text-sm"
            type="password"
            required
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="account-password-input"
          />
        </div> : null}

        {mode === "register" ? (
          <label className="flex items-start gap-3 text-sm text-ink-600">
            <input
              className="mt-1 h-4 w-4"
              type="checkbox"
              checked={productOffersConsent}
              onChange={(event) => setProductOffersConsent(event.target.checked)}
              data-testid="account-product-offers-signup"
            />
            <span>Email me occasional offers for Halo and SecureShield. Optional; I can unsubscribe at any time.</span>
          </label>
        ) : null}

        {error ? <p className="text-sm text-red-600" data-testid="account-auth-error">{error}</p> : null}
        {notice ? <p className="text-sm text-green-700" role="status">{notice}</p> : null}

        <button className="lf-btn-primary w-full justify-center" type="submit" disabled={busy} data-testid="account-submit">
          {busy ? "Please wait…" : mode === "forgot" ? "Send reset link" : mode === "login" ? "Sign in" : "Create account"}
        </button>
      </form>

      {mode === "login" ? <button className="lf-link mt-4 block" type="button" data-testid="account-forgot-password" onClick={()=>{setMode("forgot");setError("");setNotice("");}}>Forgot password?</button> : null}
      {mode === "forgot" ? <button className="lf-link mt-4" type="button" onClick={()=>{setMode("login");setError("");setNotice("");}}>Back to sign in</button> : registrationEnabled || mode === "register" ? (
        <button
          className="lf-link mt-6"
          type="button"
          onClick={() => setMode(mode === "login" ? "register" : "login")}
        >
          {mode === "login" ? "New here? Create an account" : "Already have an account? Sign in"}
        </button>
      ) : (
        <p className="mt-6 text-xs text-ink-500">New account registration is temporarily unavailable.</p>
      )}
    </div>
  );
}

const ACCOUNT_PRODUCT_NAMES = {
  securemac: "SecureShield",
  "securemac-app-store": "SecureShield",
  "securemac-pro": "SecureShield Pro",
};

function accountCatalogProduct(product) {
  const name = ACCOUNT_PRODUCT_NAMES[product.key];
  return name ? { ...product, name } : product;
}

const LEGACY_SECURESHIELD_STORE_KEYS = new Set(["securemac", "securemac-app-store"]);

const WEBSITE_RETIRED_PRODUCTS = new Set(["securemac-pro", "secureai", "secureshield"]);

const STORE_PROVIDER_LABELS = {
  apple_app_store: "Apple App Store",
  google_play: "Google Play",
  microsoft_store: "Microsoft Store",
};

const STORE_AUTHORITY_LABELS = {
  apple: "Apple",
  google: "Google",
  microsoft: "Microsoft",
};

function storeProviderLabel(purchase) {
  return STORE_PROVIDER_LABELS[purchase?.provider] || "External store";
}

function storeAuthorityLabel(purchase) {
  return STORE_AUTHORITY_LABELS[purchase?.authority]
    || STORE_AUTHORITY_LABELS[{
      apple_app_store: "apple",
      google_play: "google",
      microsoft_store: "microsoft",
    }[purchase?.provider]]
    || "the originating store";
}

function ProductRow({ product, offers = [], linkedStorePurchases = [] }) {
  const { hasEntitlement } = useCustomerAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const owned = hasEntitlement(product.key);
  const linkedEvidence = linkedStorePurchases.find(
    (purchase) => purchase.product_key === product.key
  );
  const displayName = LEGACY_SECURESHIELD_STORE_KEYS.has(product.key)
    ? `${product.name} (${linkedEvidence ? storeProviderLabel(linkedEvidence) : product.key === "securemac-app-store" ? "Apple App Store" : "legacy purchase"})`
    : product.name;
  const directOffer = offers.find(
    (offer) => offer.provider === "stripe" && offer.product_keys?.includes(product.key)
  );
  const purchasable = directOffer ? directOffer.available : product.purchasable;
  const downloadable = product.delivery?.kind === "authenticated_download";
  const deliveryAvailable = downloadable && product.delivery?.available === true;

  const buy = useCallback(async () => {
    setError("");
    setBusy(true);
    try {
      const { data } = await customerApi.post(`/products/${product.key}/checkout`);
      window.location.href = data.checkout_url;
    } catch (err) {
      setError(formatApiError(err));
      setBusy(false);
    }
  }, [product.key]);

  const download = useCallback(async () => {
    setError("");
    setBusy(true);
    try {
      const { data } = await customerApi.get(`/products/${product.key}/download`);
      window.location.href = data.download_url;
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  }, [product.key]);

  return (
    <article className="border border-ink-100 bg-white p-7">
      <div className="flex items-start justify-between gap-4">
        <div>
          <ShieldCheck className="h-6 w-6 text-electric" strokeWidth={1.7} />
          <h3 className="mt-4 font-display text-2xl font-bold">{displayName}</h3>
          <div className="mt-2 font-mono text-[10px] uppercase tracking-[.18em] text-ink-500">
            {owned ? "Owned · Active" : linkedEvidence ? `Linked · ${storeAuthorityLabel(linkedEvidence)} controlled` : purchasable ? "Not purchased yet" : "Not available yet"}
          </div>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-3">
        {owned && deliveryAvailable ? (
          <button className="lf-btn-primary" type="button" onClick={download} disabled={busy} data-testid={`download-${product.key}`}>
            <Download className="h-4 w-4" /> {busy ? "Preparing…" : `Download ${product.name}`}
          </button>
        ) : owned && downloadable ? (
          <span className="text-sm text-ink-500">Download temporarily unavailable while the approved release is prepared.</span>
        ) : owned ? (
          <span className="text-sm text-ink-600">Linked to this LightningFlow account.</span>
        ) : linkedEvidence ? (
          <span className="text-sm text-ink-600">Linked evidence only; access and billing are controlled by {storeAuthorityLabel(linkedEvidence)}.</span>
        ) : purchasable ? (
          <button className="lf-btn-primary" type="button" onClick={buy} disabled={busy} data-testid={`buy-${product.key}`}>
            {busy ? "Starting checkout…" : `Buy ${product.name}`} <ArrowUpRight className="h-4 w-4" />
          </button>
        ) : (
          <span className="text-sm text-ink-500">Not sold directly yet.</span>
        )}
      </div>
      {error ? <p className="mt-3 text-sm text-red-600">{error}</p> : null}
    </article>
  );
}

function formatPurchaseAmount(amount, currency) {
  if (!Number.isInteger(amount) || !currency) return "—";
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(amount / 100);
}

function BundleList({ bundles, products }) {
  const { hasEntitlement } = useCustomerAuth();
  if (!bundles.length) return null;
  const productNames = Object.fromEntries(
    products.map((product) => [product.key, product.name])
  );
  return (
    <section className="mt-10" data-testid="account-bundles" aria-labelledby="account-bundles-heading">
      <h2 id="account-bundles-heading" className="font-display text-2xl font-bold">Bundles</h2>
      <div className="mt-4 space-y-4">
        {bundles.map((bundle) => {
          const benefitNames = (bundle.product_keys || [])
            .map((key) => productNames[key] || key);
          const allBenefitsActive = Boolean(bundle.product_keys?.length)
            && bundle.product_keys.every((key) => hasEntitlement(key));
          return (
            <article key={bundle.key} className="border border-ink-100 bg-white p-7">
              <h3 className="font-display text-2xl font-bold">{bundle.name}</h3>
              <p className="mt-2 text-sm text-ink-600">{benefitNames.join(" · ")}</p>
              <div className="mt-4 font-mono text-[10px] uppercase tracking-[.18em] text-ink-500">
                {allBenefitsActive ? "All benefits active" : bundle.available ? "Available" : "Not available yet"}
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

function PurchaseHistory({ portal, products, bundles }) {
  const names = Object.fromEntries(
    [...products, ...bundles].map((item) => [item.key, item.name])
  );
  const linkedStorePurchases = portal?.linked_store_purchases || [];
  const linkedStoreKeys = new Set(
    linkedStorePurchases.map((purchase) => `${purchase.provider}:${purchase.product_key}`)
  );
  const purchases = [
    ...(portal?.purchases || [])
      .filter((purchase) => purchase.product !== "secureai")
      .filter((purchase) => !linkedStoreKeys.has(`${purchase.provider}:${purchase.product}`))
      .map((purchase) => ({
        ...purchase,
        product_key: purchase.product,
        source_label: purchase.provider === "stripe" ? "Direct purchase · Stripe" : purchase.provider,
        date: purchase.updated_at || purchase.created_at,
      })),
    ...linkedStorePurchases.map((purchase, index) => ({
      ...purchase,
      id: `${purchase.provider}-${index}`,
      status: purchase.status || "linked_evidence",
      source_label: storeProviderLabel(purchase),
      authority_label: storeAuthorityLabel(purchase),
      date: purchase.linked_at,
    })),
  ];
  if (!purchases.length) return null;
  return (
    <section className="mt-4 border border-ink-100 bg-white p-7" data-testid="purchase-history">
      <h2 className="font-display text-xl font-bold">Purchase history</h2>
      <div className="mt-4 divide-y divide-ink-100">
        {purchases.map((purchase) => (
          <div key={purchase.id} className="grid gap-2 py-4 text-sm sm:grid-cols-[1.4fr_1fr_auto]">
            <div>
              <div className="font-semibold text-ink-900">{names[purchase.product_key] || purchase.product_key}</div>
              <div className="mt-1 text-ink-500">{purchase.source_label}</div>
            </div>
            <div className="text-ink-600">
              <div className="capitalize">{purchase.status}</div>
              <div>{formatPurchaseAmount(purchase.amount_total, purchase.currency)}</div>
              <div>{purchase.date ? new Date(purchase.date).toLocaleDateString() : "—"}</div>
            </div>
            <div>
              {purchase.billing_management_url ? (
                <a className="lf-link" href={purchase.billing_management_url} rel="noreferrer" target="_blank">Manage in store</a>
              ) : purchase.provider === "stripe" ? (
                <span className="text-xs text-ink-500">Managed in this account</span>
              ) : null}
            </div>
            {purchase.authority_label ? (
              <p className="text-xs text-ink-500 sm:col-span-3">Linked evidence only; access and billing remain controlled by {purchase.authority_label}.</p>
            ) : null}
          </div>
        ))}
      </div>
    </section>
  );
}

function MyProducts({ proPurchaseState = "", secureAIPurchaseState = "", standardPurchaseState = "" }) {
  const { customer, portal, logout: logoutWebsite, hasEntitlement } = useCustomerAuth();
  const { logout: logoutSecureMac } = useSecureMacProAccount();
  const [linkBusy, setLinkBusy] = useState("");
  const [linkError, setLinkError] = useState("");
  // SecureShield Pro commerce and entitlement belong to the consumer platform
  // (SecureMacProAccountPanel); the website account never sells or serves it.
  const catalogProducts = (portal?.catalog?.products || []).map(accountCatalogProduct);
  const products = catalogProducts.filter(
    (product) => !WEBSITE_RETIRED_PRODUCTS.has(product.key)
      && (!LEGACY_SECURESHIELD_STORE_KEYS.has(product.key) || hasEntitlement(product.key)
        || (portal?.linked_store_purchases || []).some((purchase) => purchase.product_key === product.key))
  );
  const offers = portal?.catalog?.offers || [];
  const allBundles = portal?.catalog?.bundles || [];
  const bundles = allBundles.filter(
    (bundle) => !(bundle.product_keys || []).some((key) => WEBSITE_RETIRED_PRODUCTS.has(key))
  );
  const legacyProOwner = hasEntitlement("securemac-pro");
  const location = useLocation();
  const profileView = accountPath(location.pathname) === "/account/profile";

  const logout = useCallback(async () => {
    try {
      await logoutSecureMac();
    } finally {
      await logoutWebsite();
    }
  }, [logoutSecureMac, logoutWebsite]);

  const linkIdentity = useCallback(async (provider) => {
    setLinkBusy(provider);
    setLinkError("");
    try {
      const { data } = await customerApi.post(`/customers/auth/${provider}/link/start`);
      window.location.assign(data.authorization_url);
    } catch (err) {
      setLinkError(formatApiError(err));
      setLinkBusy("");
    }
  }, []);
  return (
    <div className="mx-auto max-w-2xl">
      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="lf-eyebrow">Your LightningFlow account</div>
          <h1 className="mt-3 font-display text-3xl font-black">{customer.name}</h1>
          <p className="mt-1 text-sm text-ink-500">{customer.email}</p>
        </div>
        <button className="lf-btn-secondary" type="button" onClick={logout} data-testid="account-sign-out">
          Sign out
        </button>
      </div>

      <nav className="mt-7 flex gap-6 border-b border-ink-200" aria-label="Account navigation">
        <Link className={`pb-3 text-sm font-semibold ${!profileView ? "border-b-2 border-electric text-ink-900" : "text-ink-500"}`} aria-current={!profileView ? "page" : undefined} to="/account">Products</Link>
        <Link className={`pb-3 text-sm font-semibold ${profileView ? "border-b-2 border-electric text-ink-900" : "text-ink-500"}`} aria-current={profileView ? "page" : undefined} to="/account/profile">Profile &amp; security</Link>
      </nav>
      {profileView ? <div data-testid="account-settings-view">
      <AccountProfile />
      <section className="mt-10 border border-ink-100 bg-white p-7" aria-labelledby="linked-identities-heading">
        <h2 id="linked-identities-heading" className="font-display text-xl font-bold">Sign-in methods</h2>
        <div className="mt-4 space-y-2 text-sm text-ink-600">
          {(portal?.linked_identities || []).map((identity) => (
            <div key={`${identity.provider}-${identity.linked_at}`} className="flex justify-between gap-4">
              <span className="capitalize">{identity.provider}</span>
              <span>{identity.email || "Linked"}</span>
            </div>
          ))}
          {!portal?.linked_identities?.length ? <p>No external sign-in methods linked.</p> : null}
        </div>
        <div className="mt-5 flex flex-wrap gap-3">
          {(portal?.identity_providers || []).map((provider) => (
            <button
              key={provider}
              className="lf-btn-secondary capitalize"
              type="button"
              disabled={Boolean(linkBusy)}
              onClick={() => linkIdentity(provider)}
            >
              {linkBusy === provider ? "Opening…" : `Link ${provider}`}
            </button>
          ))}
        </div>
        {linkError ? <p className="mt-3 text-sm text-red-600">{linkError}</p> : null}
      </section>

      <div className="mt-10"><ProductOffersChoice /></div>

      </div> : <div data-testid="account-products-view">
      {accountPath(location.pathname) === "/account/approve-device" ? (
        <p className="mt-6 text-sm text-ink-600" role="status">
          Approve your Mac under the matching product below. If its form isn't open, choose “Activate a device” or “Activate another device”. Your code is kept on this page.
        </p>
      ) : null}
      {legacyProOwner ? (
        <p className="mt-8 border border-amber-500/40 bg-amber-50 p-4 text-sm text-ink-700" data-testid="legacy-securemac-pro-notice">
          Your SecureShield Pro purchase is linked to this account. Your existing licence remains valid.
        </p>
      ) : null}

      <div className="mt-10">
        <SecureShieldAccountPanel purchaseState={standardPurchaseState} />
        <SecureMacProAccountPanel purchaseState={proPurchaseState} />
      </div>

      <div className="mt-10">
        <FortressAccountPanel />
      </div>

      <div className="mt-10">
        <SecureAIAccountPanel purchaseState={secureAIPurchaseState} />
      </div>

      <div className="mt-10 space-y-4" data-testid="account-products">
        {products.map((product) => (
          <ProductRow key={product.key} product={product} offers={offers} linkedStorePurchases={portal?.linked_store_purchases || []} />
        ))}
      </div>

      <BundleList bundles={bundles} products={catalogProducts} />

      <PurchaseHistory portal={portal} products={catalogProducts} bundles={allBundles} />
      </div>}

    </div>
  );
}

function ProductOffersChoice() {
  const { portal, refresh } = useCustomerAuth();
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const available = typeof portal?.marketing?.product_offers === "boolean";
  const enabled = portal?.marketing?.product_offers === true;

  const update = useCallback(async (next) => {
    setBusy(true);
    setMessage("");
    setError("");
    try {
      await customerApi.put("/customers/marketing-choice", { product_offers: next });
      await refresh();
      setMessage(next ? "Product offers turned on." : "Product offers turned off.");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  }, [refresh]);

  return (
    <section className="border border-ink-100 bg-white p-7" aria-labelledby="product-offers-heading" data-testid="product-offers-choice">
      <h2 id="product-offers-heading" className="font-display text-xl font-bold">Product offers</h2>
      <p className="mt-2 text-sm text-ink-600">Choose whether to receive occasional offers for Halo and SecureShield. This is separate from messages about your account and purchases.</p>
      <label className="mt-5 flex items-start gap-3 text-sm text-ink-700">
        <input
          type="checkbox"
          className="mt-1 h-4 w-4"
          checked={enabled}
          disabled={!available || busy}
          onChange={(event) => update(event.target.checked)}
          data-testid="account-product-offers-choice"
        />
        <span>Email me product offers</span>
      </label>
      {!available ? (
        <div className="mt-3 text-sm text-ink-600" role="status">
          <p>We couldn't load your email preference. Your saved choice has not changed.</p>
          <button className="lf-link mt-2" type="button" disabled={busy} onClick={async () => {
            setBusy(true);
            try { await refresh(); } finally { setBusy(false); }
          }}>{busy ? "Loading…" : "Retry preferences"}</button>
        </div>
      ) : null}
      {message ? <p className="mt-3 text-sm text-green-700" role="status">{message}</p> : null}
      {error ? <p className="mt-3 text-sm text-red-600" role="alert">{error}</p> : null}
    </section>
  );
}

function StoreLinkPanel() {
  const { refresh } = useCustomerAuth();
  const navigate = useNavigate();
  const [statusText, setStatusText] = useState("");
  const [busy, setBusy] = useState(false);

  const complete = useCallback(async (event) => {
    event.preventDefault();
    setBusy(true);
    setStatusText("");
    try {
      const { data } = await customerApi.post("/customers/store-links/apple/complete", {});
      if (data.status === "processing") {
        setStatusText("Verification is already in progress. Try again shortly.");
        return;
      }
      await refresh();
      navigate("/account", { replace: true });
    } catch (err) {
      setStatusText(formatApiError(err));
    } finally {
      setBusy(false);
    }
  }, [navigate, refresh]);

  return (
    <form className="mb-8 border border-electric/30 bg-electric/5 p-6" onSubmit={complete}>
      <div className="lf-eyebrow">Apple App Store purchase</div>
      <h2 className="mt-2 font-display text-2xl font-bold">Link an earlier SecureShield purchase</h2>
      <p className="mt-2 text-sm text-ink-600">SecureShield costs US$4.99 once. App Store purchases remain controlled by Apple; link an existing purchase after Apple verifies it.</p>
      <div className="mt-5 flex gap-3">
        <button className="lf-btn-primary" type="submit" disabled={busy}>{busy ? "Verifying…" : "Verify and link"}</button>
      </div>
      {statusText ? <p className="mt-3 text-sm text-red-600">{statusText}</p> : null}
    </form>
  );
}

function EmailOnboarding({ onboarding }) {
  const location = useLocation();
  const [email, setEmail] = useState(onboarding.email_suggestion || "");
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = useCallback(async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await customerApi.post("/customers/onboarding/email", {
        email,
        ...(accountPath(location.pathname) === "/account/approve-device" ? { return_to: `${accountPath(location.pathname)}${location.search}` } : {}),
      });
      setNotice("Check your email for the verification link. You cannot transact until it is verified.");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  }, [email, location.pathname, location.search]);

  return (
    <form className="mx-auto max-w-md border border-ink-100 bg-white p-7" onSubmit={submit}>
      <div className="lf-eyebrow">Complete your account</div>
      <h1 className="mt-3 font-display text-3xl font-black">Verify your email.</h1>
      <p className="mt-3 text-sm text-ink-600">Microsoft did not provide a verified email. Add one before this account can access products or purchases.</p>
      <input className="mt-6 w-full border border-ink-200 px-4 py-3" type="email" required value={email} onChange={(event) => setEmail(event.target.value)} />
      <button className="lf-btn-primary mt-4 w-full justify-center" type="submit" disabled={busy}>{busy ? "Sending…" : "Send verification email"}</button>
      {notice ? <p className="mt-3 text-sm text-green-700">{notice}</p> : null}
      {error ? <p className="mt-3 text-sm text-red-600">{error}</p> : null}
    </form>
  );
}

export default function Account() {
  const { customer, onboarding, ready } = useCustomerAuth();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const purchaseState = searchParams.get("purchase");
  // The consumer platform tags its Checkout return with product=securemac-pro.
  const proPurchaseState = searchParams.get("product") === "securemac-pro" ? purchaseState || "" : "";
  const secureAIPurchaseState = searchParams.get("product") === "secureai" ? purchaseState || "" : "";
  const standardPurchaseState = searchParams.get("product") === "secureshield" ? purchaseState || "" : "";
  const websitePurchaseState = proPurchaseState || secureAIPurchaseState || standardPurchaseState ? "" : purchaseState;
  const storeLinkActive = accountPath(location.pathname) === "/account/link-store";
  const deviceApprovalActive = accountPath(location.pathname) === "/account/approve-device";

  return (
    <div className="bg-ink-50/40 text-ink-900">
      <SEO title="Your LightningFlow Account" description="Manage SecureShield Pro, Halo SuperVPN and SecureAI." noIndex />
      <section className="lf-section min-h-[70vh]">
        <div className="lf-container">
          {websitePurchaseState === "success" ? (
            <p className="mx-auto mb-8 max-w-2xl border border-electric/30 bg-electric/5 p-4 text-sm text-ink-700">
              Checkout returned; payment confirmation is pending. Your purchase appears below only after confirmation.
            </p>
          ) : null}
          {!ready ? (
            <div className="text-center text-sm text-ink-500" data-testid="account-loading">Loading…</div>
          ) : onboarding ? (
            <EmailOnboarding onboarding={onboarding} />
          ) : customer ? (
            <>
              {storeLinkActive ? <StoreLinkPanel /> : null}
              <MyProducts
                proPurchaseState={proPurchaseState}
                secureAIPurchaseState={secureAIPurchaseState}
                standardPurchaseState={standardPurchaseState}
              />
            </>
          ) : (
            <>
              {storeLinkActive ? (
                <p className="mx-auto mb-6 max-w-md border border-electric/30 bg-electric/5 p-4 text-sm text-ink-700">
                  Sign in first, then we’ll verify and link this App Store purchase.
                </p>
              ) : null}
              {deviceApprovalActive ? (
                <p className="mx-auto mb-6 max-w-md border border-electric/30 bg-electric/5 p-4 text-sm text-ink-700">
                  Sign in to your LightningFlow account before approving this Mac. The code will remain on this page.
                </p>
              ) : null}
              <AuthForms />
            </>
          )}
        </div>
      </section>
    </div>
  );
}
